/** DSD 워크북 - IXD 값 채우기 진입 페이지: 워크북 + 기존/신규 IXD로 비교 시작 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, GitCompare, Loader2 } from "lucide-react";

import { uploadIxdPreview } from "@/apis/ixd-editor/route";
import { logUserAction, type UserActionFile } from "@/apis/logs/route";
import { isInvalidIxdStructureError } from "@/apis/errors";
import type { DsdFile } from "@/types/dsd";
import { FileUploadSection } from "@/components/file-upload-section";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import { NewChip } from "@/components/common/new-chip";
import { CompareFailedModal } from "@/components/modals/compare-failed-modal";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import { Button } from "@/toggle-ui-kit/button";
import { LeftSidebar } from "@/components/app-shell";
import { WorkbookPicker } from "@/components/ixd-workbook-compare/workbook-picker";
import { ExistingIxdReportPicker } from "@/components/existing-ixd-report-picker";
import queries from "@/queries";
import { pushModal } from "@/stores/modal";
import {
  isIxdMatchPreviewTabMessage,
  type IxdMatchPreviewTabStatus,
} from "@/utils/ixd-workbook-match/tab-messages";
import { openPendingTab } from "@/utils/pending-tab";
import { SelectedIxdFileCard } from "./selected-ixd-file-card";

type IxdSourceMode = "existing-report" | "upload";

const MATCH_SOURCE_PICKER_CLASS = "h-full w-full !box-border overflow-hidden";

const EMBEDDED_PICKER_CLASS =
  "min-h-0 flex-1 w-full !box-border overflow-hidden border-0 !p-0";

export const IxdWorkbookMatchPage = () => {
  const queryClient = useQueryClient();
  const [selectedWorkbook, setSelectedWorkbook] = useState<DsdFile | null>(
    null,
  );
  const workbookId = selectedWorkbook?.id ?? null;
  const [reportId, setReportId] = useState<string | null>(null);
  const [ixdFile, setIxdFile] = useState<File | null>(null);
  const [sourceMode, setSourceMode] = useState<IxdSourceMode>("upload");
  const [isComparisonPending, setIsComparisonPending] = useState(false);
  const comparisonTabRef = useRef<Window | null>(null);
  const comparisonWorkbookIdRef = useRef<string | null>(null);

  const { data: ixdReportsData } = useQuery({
    ...queries.ixdEditorReports(),
    enabled: sourceMode === "existing-report",
    staleTime: 60 * 1000,
  });
  const selectedExistingReport = ixdReportsData?.reports.find(
    (report) => report.reportId === reportId,
  );

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadIxdPreview(file),
    onSuccess: ({ reportId: uploadedReportId }, uploadedFile) => {
      logValueSyncStarted({
        filename: uploadedFile.name,
        ixdReportId: uploadedReportId,
      });
      void queryClient.invalidateQueries({
        queryKey: queries.ixdEditorReports().queryKey,
      });
      const tab = comparisonTabRef.current;
      const selectedWorkbookId = comparisonWorkbookIdRef.current;
      if (!selectedWorkbookId || !tab || tab.closed) {
        setIsComparisonPending(false);
        return;
      }
      const searchParams = new URLSearchParams({
        workbookId: selectedWorkbookId,
        reportId: uploadedReportId,
      });
      tab.location.replace(`/ixd-editor/view?${searchParams.toString()}`);
    },
    onError: (error) => {
      comparisonTabRef.current?.close();
      comparisonTabRef.current = null;
      comparisonWorkbookIdRef.current = null;
      setIsComparisonPending(false);
      if (isInvalidIxdStructureError(error)) {
        pushModal(UploadFailedModal);
      } else {
        pushModal(CompareFailedModal);
      }
    },
  });

  useEffect(() => {
    const handleTabStatus = (event: MessageEvent<unknown>) => {
      const tab = comparisonTabRef.current;
      if (
        event.origin !== window.location.origin ||
        event.source !== tab ||
        !isIxdMatchPreviewTabMessage(event.data)
      ) {
        return;
      }

      const status: IxdMatchPreviewTabStatus = event.data.status;
      comparisonTabRef.current = null;
      comparisonWorkbookIdRef.current = null;
      setIsComparisonPending(false);
      if (status === "failed") {
        tab?.close();
        pushModal(CompareFailedModal);
      }
    };

    window.addEventListener("message", handleTabStatus);
    return () => window.removeEventListener("message", handleTabStatus);
  }, []);

  useEffect(() => {
    if (!isComparisonPending) return;
    const intervalId = window.setInterval(() => {
      const tab = comparisonTabRef.current;
      if (tab && !tab.closed) return;
      comparisonTabRef.current = null;
      comparisonWorkbookIdRef.current = null;
      setIsComparisonPending(false);
    }, 500);
    return () => window.clearInterval(intervalId);
  }, [isComparisonPending]);

  /**
   * 값 편집기 헤더의 "워크북 값 채우기" 모달에 있던 로그를 이 화면으로 옮겼다.
   * 라벨은 그대로 둔다 — Slack 알림 제목이 집계 키라 바꾸면 기존 통계가 끊긴다.
   *
   * 워크북은 그 자체가 파일이 아니라 분석 결과물이라, 워크북을 만들어낸 업로드 DSD 를
   * 지목해 링크를 건다. 파일 등록 도입 전 워크북은 그 id 가 없어 이름만 남는다.
   * IXD 는 새로 올렸든 기존을 골랐든 report 로 저장돼 있어 항상 링크가 붙는다.
   */
  const logValueSyncStarted = (ixd: UserActionFile | null) => {
    const files: UserActionFile[] = [];
    if (selectedWorkbook) {
      files.push({
        filename: selectedWorkbook.name,
        dsdFileId: selectedWorkbook.uploadedFileId ?? undefined,
      });
    }
    if (ixd) {
      files.push(ixd);
    }
    logUserAction("IXD 태깅값 채우기", files);
  };

  const selectedIxdReady =
    sourceMode === "existing-report" ? !!reportId : !!ixdFile;
  const canStart = !!workbookId && selectedIxdReady;

  const openComparisonTab = () => {
    const tab = openPendingTab({
      title: "DSD 워크북 - IXD 값 채우기 준비 중...",
      message: "비교 화면을 준비하고 있습니다...",
      preserveOpener: true,
    });
    if (!tab) {
      pushModal(CompareFailedModal);
      return null;
    }
    comparisonTabRef.current = tab;
    comparisonWorkbookIdRef.current = workbookId;
    setIsComparisonPending(true);
    return tab;
  };

  const handleStart = () => {
    if (!canStart || !workbookId) return;
    const tab = openComparisonTab();
    if (!tab) return;

    if (sourceMode === "upload") {
      if (!ixdFile) return;
      uploadMutation.mutate(ixdFile);
      return;
    }

    if (!reportId) return;
    logValueSyncStarted({
      filename: selectedExistingReport?.originalFilename ?? "",
      ixdReportId: reportId,
    });
    const searchParams = new URLSearchParams({ workbookId, reportId });
    tab.location.replace(`/ixd-editor/view?${searchParams.toString()}`);
  };

  return (
    <div className="flex h-screen w-full">
      <LeftSidebar activeMenu="ixd-workbook-match" />
      <div className="flex-1 min-w-0 flex flex-col items-center overflow-auto bg-white py-32">
        <div className="flex flex-col gap-9 items-center w-full p-16 min-w-[784px]">
          <div className="flex flex-col gap-2 items-start max-w-[1144px] w-full">
            <div className="flex items-center gap-2">
              <h1 className="title-2-bold text-label-normal">
                DSD 워크북 - IXD 값 채우기
              </h1>
              <NewChip color="indigo" size="large" />
            </div>
            <p className="body-1-normal-medium text-label-neutral">
              DSD 워크북 파일을 선택하고 IXD 파일을 업로드하여 항목별 값을 비교
              · 수정하세요.
            </p>
          </div>

          <div className="flex flex-col gap-5 items-center w-full">
            <div className="flex items-center justify-end gap-2 max-w-[1144px] w-full">
              <Button
                variant="solid"
                color="secondary"
                size={36}
                className="bg-blue-500 text-static-white"
                leftIcon={
                  uploadMutation.isPending || isComparisonPending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <GitCompare size={18} />
                  )
                }
                disabled={
                  !canStart || uploadMutation.isPending || isComparisonPending
                }
                onClick={handleStart}
              >
                {uploadMutation.isPending
                  ? "IXD 업로드 중..."
                  : isComparisonPending
                    ? "비교 준비 중..."
                    : "비교하기"}
              </Button>
            </div>

            <div className="flex h-[500px] w-full max-w-[1144px] items-center gap-3">
              <div className="flex h-full min-w-0 flex-1">
                <WorkbookPicker
                  title="DSD 워크북 불러오기"
                  description=""
                  searchPlaceholder="보고서 이름으로 검색"
                  selectedWorkbookId={workbookId}
                  onSelect={(_workbookId, workbook) =>
                    setSelectedWorkbook(workbook)
                  }
                  className={MATCH_SOURCE_PICKER_CLASS}
                  // 설계 QA 2번: 테두리 Primary/Color, 내부 --blue-50
                  selectedTone="blue"
                  listHeight="fill"
                />
              </div>

              <ArrowRightLeft
                className="size-5 shrink-0 -scale-x-100 text-label-normal"
                strokeWidth={2.5}
              />

              <div className="flex h-full min-w-0 flex-1 flex-col rounded-[10px] border-2 border-dashed border-line-normal bg-background-base p-4">
                <div className="mb-3 flex items-center justify-between border-b border-line-normal pb-3">
                  <span className="label-1-normal-medium text-label-normal">
                    {sourceMode === "upload" ? "IXD 업로드" : "IXD 선택"}
                  </span>
                  <div
                    role="group"
                    aria-label="IXD 불러오기 방식"
                    className="flex items-center rounded-[8px] bg-background-neutral-normal p-1"
                  >
                    <button
                      type="button"
                      aria-pressed={sourceMode === "upload"}
                      className={`h-7 rounded-[6px] px-3 label-2-medium transition-colors ${
                        sourceMode === "upload"
                          ? "bg-background-base text-label-normal shadow-sm"
                          : "text-label-alternative hover:text-label-normal"
                      }`}
                      onClick={() => setSourceMode("upload")}
                    >
                      새 IXD 업로드
                    </button>
                    <button
                      type="button"
                      aria-pressed={sourceMode === "existing-report"}
                      className={`h-7 rounded-[6px] px-3 label-2-medium transition-colors ${
                        sourceMode === "existing-report"
                          ? "bg-background-base text-label-normal shadow-sm"
                          : "text-label-alternative hover:text-label-normal"
                      }`}
                      onClick={() => setSourceMode("existing-report")}
                    >
                      이전 IXD 불러오기
                    </button>
                  </div>
                </div>

                <div className="flex min-h-0 flex-1">
                  {sourceMode === "existing-report" ? (
                    /* 긴 IXD 이름이 flex item의 최소 너비를 키우지 않도록 한다. */
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
                      <ExistingIxdReportPicker
                        selectedReportId={reportId}
                        onSelect={setReportId}
                        enabled
                        title=""
                        description=""
                        showSelectionIndicator
                        showMetadata={false}
                        className={EMBEDDED_PICKER_CLASS}
                        selectedTone="blue"
                        listHeight="fill"
                      />
                      {selectedExistingReport && (
                        <SelectedIxdFileCard
                          fileName={selectedExistingReport.originalFilename}
                          periodYear={selectedExistingReport.periodYear}
                          periodType={selectedExistingReport.periodType}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="flex min-h-0 flex-1 flex-col gap-3">
                      <FileUploadSection
                        label="IXD 파일"
                        accept=".ixd"
                        onFileSelect={setIxdFile}
                        isLoading={uploadMutation.isPending}
                        description="*.ixd 형식"
                        accentColor="purple"
                        className="min-h-0 w-full !box-border border-0 bg-background-neutral-neutral hover:bg-background-gray-alternative"
                      />
                      {ixdFile && (
                        <SelectedIxdFileCard fileName={ixdFile.name} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <IxdUploadNotice className="w-full max-w-[1144px]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IxdWorkbookMatchPage;
